<template>
  <div class="pv-page">
    <div class="pv-hero">
      <div class="pv-brand">
        <div class="pv-mark" aria-hidden="true">HB</div>
        <div>
          <p class="pv-eyebrow">Homa Bay County Government</p>
          <h1>Public Vacancy</h1>
        </div>
      </div>
    </div>

    <div class="pv-body">
      <div v-if="loading" class="pv-card center">
        <div class="spin"></div>
        <p>Loading vacancy…</p>
      </div>

      <div v-else-if="error" class="pv-card center">
        <h2>Vacancy unavailable</h2>
        <p>{{ error }}</p>
      </div>

      <div v-else-if="vacancy" class="pv-card">
        <div class="pv-head">
          <div>
            <p class="ref" v-if="vacancy.vacancy_ref_no">Ref: {{ vacancy.vacancy_ref_no }}</p>
            <h2>{{ vacancy.title }}</h2>
            <p class="meta">
              <span v-if="vacancy.job_location"><i class="bx bx-map"></i> {{ vacancy.job_location }}</span>
              <span v-if="vacancy.salary_scale"><i class="bx bx-money"></i> {{ vacancy.salary_scale }}</span>
              <span v-if="deadline"><i class="bx bx-calendar"></i> Closes {{ deadline }}</span>
            </p>
          </div>
          <span class="pill">Open</span>
        </div>

        <section v-if="vacancy.summary">
          <h3>Summary</h3>
          <p class="prose">{{ vacancy.summary }}</p>
        </section>

        <section v-if="vacancy.job_description">
          <h3>Job description</h3>
          <p class="prose" v-html="nl(vacancy.job_description)"></p>
        </section>

        <div class="grid">
          <div v-if="vacancy.min_education">
            <h4>Minimum education</h4>
            <p>{{ vacancy.min_education }}</p>
          </div>
          <div v-if="vacancy.years_experience != null && vacancy.years_experience !== ''">
            <h4>Years of experience</h4>
            <p>{{ vacancy.years_experience }}</p>
          </div>
          <div v-if="vacancy.num_positions">
            <h4>Positions</h4>
            <p>{{ vacancy.num_positions }}</p>
          </div>
          <div v-if="vacancy.advert_date">
            <h4>Advert date</h4>
            <p>{{ vacancy.advert_date }}</p>
          </div>
        </div>

        <section v-if="vacancy.professional_certs">
          <h3>Professional certifications</h3>
          <p class="prose">{{ vacancy.professional_certs }}</p>
        </section>

        <section v-if="vacancy.specific_skills">
          <h3>Skills</h3>
          <p class="prose">{{ vacancy.specific_skills }}</p>
        </section>

        <div class="pv-cta">
          <p>To apply, submit your application through the County HR office or the official application channels published with this advert.</p>
          <button type="button" class="btn" @click="share">Share this vacancy</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { execute2, authUrl } from "@/api";

export default {
  name: "PublicVacancy",
  data() {
    return {
      loading: true,
      error: "",
      vacancy: null
    };
  },
  computed: {
    deadline() {
      return this.vacancy?.application_deadline || this.vacancy?.closing_date || "";
    },
    token() {
      return this.$route.params.token || this.$route.query.token || "";
    }
  },
  watch: {
    "$route.params.token"() {
      this.load();
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    nl(text) {
      return String(text || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>");
    },
    async load() {
      this.loading = true;
      this.error = "";
      this.vacancy = null;
      const token = String(this.token || "").trim();
      if (!token) {
        this.error = "Invalid vacancy link.";
        this.loading = false;
        return;
      }
      try {
        const fd = new FormData();
        fd.append("function", "getPublicVacancy");
        fd.append("token", token);
        const res = await execute2(fd, authUrl);
        if (res.data?.success && res.data.data?.vacancy) {
          this.vacancy = res.data.data.vacancy;
        } else {
          this.error = res.data?.message || "This vacancy is closed or the link is invalid.";
        }
      } catch (e) {
        this.error = "Could not load vacancy. Please try again later.";
      } finally {
        this.loading = false;
      }
    },
    async share() {
      const url = window.location.href;
      try {
        if (navigator.share) {
          await navigator.share({ title: this.vacancy?.title || "Vacancy", url });
          return;
        }
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          alert("Link copied. You can paste it on social media or your website.");
          return;
        }
        window.prompt("Copy link:", url);
      } catch (_) {
        window.prompt("Copy link:", url);
      }
    }
  }
};
</script>

<style scoped>
.pv-page {
  min-height: 100vh;
  background:
    radial-gradient(1200px 400px at 10% -10%, rgba(14, 116, 144, 0.18), transparent),
    linear-gradient(180deg, #f0f7fb 0%, #e8eef3 40%, #f8fafc 100%);
  color: #0f172a;
  font-family: "Segoe UI", system-ui, sans-serif;
}
.pv-hero {
  background: linear-gradient(135deg, #0c2d48 0%, #164e63 55%, #0e7490 100%);
  color: #fff;
  padding: 28px 20px 36px;
}
.pv-brand {
  max-width: 920px;
  margin: 0 auto;
  display: flex;
  gap: 16px;
  align-items: center;
}
.pv-mark {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.14);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  letter-spacing: 0.04em;
}
.pv-eyebrow {
  margin: 0 0 4px;
  opacity: 0.85;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-size: 12px;
}
.pv-hero h1 {
  margin: 0;
  font-size: clamp(1.4rem, 3vw, 1.9rem);
  font-weight: 700;
}
.pv-body {
  max-width: 920px;
  margin: -18px auto 40px;
  padding: 0 16px;
}
.pv-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.08);
  padding: 24px;
  border: 1px solid #e2e8f0;
}
.pv-card.center {
  text-align: center;
  padding: 48px 24px;
}
.pv-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 16px;
  margin-bottom: 18px;
}
.pv-head h2 {
  margin: 4px 0 10px;
  color: #0c2d48;
  font-size: 1.5rem;
}
.ref {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}
.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 18px;
  margin: 0;
  color: #475569;
  font-size: 14px;
}
.meta i {
  margin-right: 4px;
  vertical-align: middle;
}
.pill {
  background: #dcfce7;
  color: #166534;
  font-weight: 700;
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 999px;
  white-space: nowrap;
}
section {
  margin-bottom: 18px;
}
h3 {
  margin: 0 0 8px;
  font-size: 1rem;
  color: #0c2d48;
}
h4 {
  margin: 0 0 4px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}
.prose {
  margin: 0;
  line-height: 1.6;
  white-space: pre-wrap;
  color: #334155;
}
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 14px;
  margin: 18px 0;
  padding: 14px;
  background: #f8fafc;
  border-radius: 12px;
}
.grid p {
  margin: 0;
  font-weight: 600;
  color: #0f172a;
}
.pv-cta {
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
}
.pv-cta p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  max-width: 540px;
}
.btn {
  border: none;
  background: #0c2d48;
  color: #fff;
  font-weight: 600;
  padding: 10px 16px;
  border-radius: 10px;
  cursor: pointer;
}
.btn:hover {
  background: #164e63;
}
.spin {
  width: 36px;
  height: 36px;
  border: 3px solid #cbd5e1;
  border-top-color: #0e7490;
  border-radius: 50%;
  margin: 0 auto 12px;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (max-width: 640px) {
  .pv-head {
    flex-direction: column;
  }
  .pv-cta {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
